import { FC, ReactNode } from 'react'
import {
  IconBrokenChainLink,
  GreyGrey10,
  GreyGrey40,
  GreyGrey60,
  GreyGrey80,
  GreyWhite,
  Primary13SemiBold,
  Primary13Regular,
  ShadowXs,
  Button,
  Tooltip,
  IconArrowUpRight,
  useModalState,
  Box,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import { inflect } from 'inflection'

export const ExistingEntityLink: FC<{
  externalEntityLabel: string
  serviceName: string
  unlinkModal?: (modalProps: { onClose: () => void }) => ReactNode
  showUnlinkButton?: boolean
  linkedEntity: {
    href?: string
    canUnlink: boolean
    label: string
    isPending: boolean
  }
  warnings?: Array<ReactNode>
  'data-testid'?: string
}> = ({ showUnlinkButton = true, ...props }) => {
  const hasLink = Boolean(props.linkedEntity.href)
  const hasWarnings = Boolean(props.warnings?.length)
  const { isModalOpen, openModal, closeModal } = useModalState()

  return (
    <Grid
      gridTemplateColumns={showUnlinkButton ? '1fr auto' : '1fr'}
      gridRowGap={'12px'}
      gridTemplateAreas={
        showUnlinkButton
          ? `
        "description unlink"
        ${hasLink ? '"view view"' : ''}
        ${hasWarnings ? '"warnings warnings"' : ''}
      `
          : `
        "description"
        ${hasLink ? '"view"' : ''}
        ${hasWarnings ? '"warnings"' : ''}
      `
      }
      cursor="default"
      gridColumnGap={'8px'}
      borderRadius="8px"
      border={hasWarnings ? `1px solid ${GreyGrey40}` : 'none'}
      background={hasWarnings ? 'transparent' : GreyWhite}
      boxShadow={ShadowXs}
      padding="12px"
      backgroundColor={hasWarnings ? 'transparent' : GreyGrey10}
      data-testid={props['data-testid']}
    >
      <GridItem
        gridArea="description"
        display="flex"
        flexDirection="column"
        gap="4px"
      >
        <Box
          {...Primary13SemiBold}
          color={GreyGrey80}
          _firstLetter={{ textTransform: 'uppercase' }}
        >
          {inflect(props.externalEntityLabel, 1)}
        </Box>
        <Box
          {...Primary13Regular}
          color={GreyGrey60}
          data-testid={
            props['data-testid'] ? `${props['data-testid']}.label` : undefined
          }
        >
          {props.linkedEntity.label}
        </Box>
      </GridItem>
      {showUnlinkButton && (
        <GridItem gridArea="unlink">
          {props.linkedEntity.canUnlink && !props.linkedEntity.isPending ? (
            <>
              <Tooltip label={`Unlink ${props.externalEntityLabel}`}>
                <IconBrokenChainLink
                  color={GreyGrey60}
                  data-testid={`${props.externalEntityLabel}.unlink`}
                  cursor="pointer"
                  onClick={openModal}
                />
              </Tooltip>
              {isModalOpen && props.unlinkModal?.({ onClose: closeModal })}
            </>
          ) : (
            <Tooltip
              label={`Cannot unlink ${props.linkedEntity.isPending ? 'pending ' : ''}${props.externalEntityLabel}`}
            >
              <IconBrokenChainLink
                data-disabled={true}
                color={GreyGrey40}
                data-testid={`${props.externalEntityLabel}.unlink`}
              />
            </Tooltip>
          )}
        </GridItem>
      )}
      {Boolean(props.linkedEntity.href) && (
        <GridItem gridArea="view">
          <a href={props.linkedEntity.href} target="_blank" rel="noreferrer">
            <Button
              variant="secondary"
              style={{ width: '100%' }}
              disabled={props.linkedEntity.isPending}
              data-testid={`${props['data-testid']}.viewButton`}
            >
              <Flex alignItems="center" gap="8px">
                View in {props.serviceName}
                <IconArrowUpRight color={GreyGrey80} />
              </Flex>
            </Button>
          </a>
        </GridItem>
      )}
      {Boolean(props.warnings) &&
        props.warnings?.map((warning, index) => (
          <GridItem key={index} gridArea="warnings">
            {warning}
          </GridItem>
        ))}
    </Grid>
  )
}
